import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { submitContact } from '../lib/api';
import {
  ArrowRight,
  Mail,
  TrendingUp,
  FileText,
  BarChart3,
  Shield,
  Users,
  CheckCircle2,
  Check,
  Sparkles,
  Eye,
  DollarSign,
  Clipboard,
  ShieldCheck,
  BarChart,
  Phone,
  MessageSquare,
  Menu,
  X,
} from 'lucide-react';

// High-end executive and business photography
const IMG_HERO_BUILDING = 'https://images.unsplash.com/photo-1601923112035-3e4819c82317?w=1920&q=90';
const IMG_EXECUTIVE_1 = 'https://images.unsplash.com/photo-1718209881007-c0ecdfc00f9d?w=800&q=85';
const IMG_EXECUTIVE_2 = 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&q=85';
const IMG_BOARDROOM = 'https://images.unsplash.com/photo-1758518730037-a16581a040e8?w=900&q=85';
const IMG_OFFICE_INTERIOR = 'https://images.unsplash.com/photo-1758448093806-88b2089068ab?w=800&q=85';
const IMG_MEETING = 'https://images.unsplash.com/photo-1758691736580-a41e0cfe9e9f?w=800&q=85';
const IMG_WORKING = 'https://images.unsplash.com/photo-1622151834677-70f982c9adef?w=800&q=85';

export default function LandingPage() {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({ 
    name: '', 
    email: '', 
    company: '', 
    phone: '', 
    message: '' 
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll to add header background
  React.useEffect(() => {
    const handleScroll = () => {
      // Detect if scrolled past hero section (roughly 800px)
      if (window.scrollY > 800) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    setContactLoading(true);
    try {
      await submitContact(contactForm);
      toast.success('Thank you! We\'ll be in touch shortly.');
      setContactForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ===== PREMIUM HEADER ===== */}
      <header className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-white/10 transition-colors duration-300 ${isScrolled ? 'bg-[#090D17]/90' : ''} ${mobileMenuOpen ? 'bg-[#090D17]' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex items-center justify-between h-[74px]">
            {/* Logo */}
            <div className="flex items-center gap-[5px] md:gap-3">
              <img src="https://customer-assets-jai6qajn.emergentagent.net/job_38d96a59-41a3-4967-8296-b7ba8cd6e1a1/artifacts/rrpmz7yf_logo%20%287%29.png" alt="OwnerSide" className="h-[27px] w-[27px] md:h-9 md:w-9 object-contain" data-testid="header-logo" />
              <span className="font-heading text-lg font-bold text-white">OwnerSide</span>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-white/70 hover:text-white transition-colors text-sm font-medium"
              >
                Services
              </button>
              <button
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-white/70 hover:text-white transition-colors text-sm font-medium"
              >
                How It Works
              </button>
              <button
                onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-white/70 hover:text-white transition-colors text-sm font-medium"
              >
                FAQ
              </button>
            </nav>

            <div className="flex items-center gap-2 sm:gap-4">
              {/* CTA Button */}
              <Button
                onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#0E7C72] hover:bg-[#0d6d64] text-white h-9 px-3 text-xs sm:h-11 sm:px-6 sm:text-sm rounded-xl"
                data-testid="header-cta-button"
              >
                Schedule a Consultation
              </Button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden flex items-center justify-center h-9 w-9 text-white/80 hover:text-white transition-colors"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#090D17] border-t border-white/10" data-testid="mobile-menu-dropdown">
            <nav className="flex flex-col px-6 py-4 gap-4">
              <button
                onClick={() => { document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}
                className="text-white/70 hover:text-white transition-colors text-sm font-medium text-left"
              >
                Services
              </button>
              <button
                onClick={() => { document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}
                className="text-white/70 hover:text-white transition-colors text-sm font-medium text-left"
              >
                How It Works
              </button>
              <button
                onClick={() => { document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}
                className="text-white/70 hover:text-white transition-colors text-sm font-medium text-left"
              >
                FAQ
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* ===== PREMIUM HERO ===== */}
      <section className="relative bg-gradient-to-br from-[#090D17] via-[#0a1220] to-[#090D17] text-white overflow-hidden pt-[74px]">
        {/* Animated grid background */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDMpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40"></div>
        
        {/* Gradient overlay accent - reduced brightness */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#0E7C72]/10 to-transparent blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-4 pb-16 sm:pt-8 sm:pb-24 lg:pb-28">
          {/* Centered Content - moved up to show stats without scrolling */}
          <div className="max-w-5xl mx-auto text-center flex flex-col items-center gap-8">
            <div className="pb-0 lg:pb-[20px]">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mt-[36px] sm:mt-[90px]">
                <Sparkles className="h-4 w-4 text-[#0E7C72]" />
                <span className="font-mono uppercase tracking-[0.25em] text-[10px] sm:text-xs text-white/60">
                  Your External Owner's Office
                </span>
              </div>
            </div>
            
            <h1 className="font-heading text-[35px] sm:text-[61px] lg:text-[77px] font-bold tracking-tight leading-[1.1]">
              We help business owners
              <br className="hidden sm:inline" />
              <span className="text-[#0E7C72]"> understand, control and improve their business.</span>
            </h1>
            
            <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto lg:max-w-[950px] -mb-[15px] sm:mb-[20px]">
              OwnerSide provides independent executive support across finance, operations, reporting and special projects—helping business owners make better decisions with greater <br className="sm:hidden" />clarity and confidence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 pb-[15px]">
              <Button 
                size="lg" 
                onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })} 
                className="bg-[#0E7C72] hover:bg-[#0d6d64] text-white h-14 px-8 text-base rounded-xl shadow-lg shadow-[#0E7C72]/20"
                data-testid="hero-cta-button"
              >
                Schedule a Consultation
              </Button>
              <button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-white/70 hover:text-white transition-colors text-base font-medium flex items-center gap-2 group"
              >
                Learn More
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FULL-WIDTH IMAGE BREAK ===== */}
      <section className="w-full h-[214px] sm:h-[378px]">
        <img
          src="https://customer-assets-39nsmqrw.emergentagent.net/job_67bee0b4-0865-415b-a742-4d42f51c8383/artifacts/1iuc2wtl_0e4cdf8b-1b0b-4db1-8fe0-c02d2f5e0907.png"
          alt="Modern architectural buildings"
          className="w-full h-full object-cover object-[center_center] lg:object-[center_60%]"
        />
      </section>

      {/* ===== THE COORDINATION GAP ===== */}
      <section className="py-[50px] sm:py-16 lg:py-[90px] bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          {/* Header */}
          <div className="max-w-5xl mx-auto mb-[54px] sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 mb-8">
              <span className="font-mono uppercase tracking-[0.2em] text-xs text-slate-500">
                The Coordination Gap
              </span>
            </div>
            <h2 className="font-heading text-[33px] sm:text-[3.1875rem] font-bold tracking-tight text-slate-950 leading-[1.05] mb-0">
              Every business owner has experts. <br className="hidden lg:inline" />Very few have someone connecting everything together.
            </h2>
          </div>

          {/* Content Rows */}
          <div className="max-w-5xl mx-auto space-y-0">
            {/* Accountant */}
            <div className="py-[43px] sm:py-12 border-t border-slate-200">
              <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start">
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-slate-950 mb-4">
                    Accountant
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed max-w-2xl lg:max-w-[920px]">
                    Handles compliance reporting, prepares financial statements, files tax returns and ensures <br className="hidden lg:inline" />the books balance. Critical work, but focused on historical records and regulatory requirements.
                  </p>
                </div>
                <div className="text-right lg:pt-1">
                  <span className="font-mono text-[13px] tracking-wide text-slate-400 uppercase">
                    Financial Reporting
                  </span>
                </div>
              </div>
            </div>

            {/* Lawyer */}
            <div className="py-[43px] sm:py-12 border-t border-slate-200">
              <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start">
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-slate-950 mb-4">
                    Lawyer
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
                    Drafts agreements, reviews contracts, provides legal counsel and protects against risk. Essential advice, but delivered project-by-project as specific needs arise.
                  </p>
                </div>
                <div className="text-right lg:pt-1">
                  <span className="font-mono text-[13px] tracking-wide text-slate-400 uppercase">
                    Legal Protection
                  </span>
                </div>
              </div>
            </div>

            {/* OwnerSide - Highlighted */}
            <div className="py-[43px] sm:py-12 border-t border-slate-200 bg-slate-50/50 -mx-6 sm:-mx-10 lg:-mx-16 px-6 sm:px-10 lg:px-16">
              <div className="max-w-5xl mx-auto">
                <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="font-heading text-2xl font-semibold text-slate-950">
                        OwnerSide
                      </h3>
                      <span className="px-3 py-1 rounded-full bg-[#0E7C72] text-white text-xs font-medium">
                        Your Team
                      </span>
                    </div>
                    <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
                      Coordinates everything from the owner's perspective. Follows up on advisor recommendations, tracks contracts and obligations, prepares financing packages, monitors receivables, builds management reports and ensures nothing falls through the cracks. Available for focused projects or ongoing executive support.
                    </p>
                  </div>
                  <div className="text-right lg:pt-1">
                    <span className="font-mono text-[13px] tracking-wide text-[#0E7C72] uppercase font-medium">
                      Operational Coordination
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom border */}
            <div className="border-t border-slate-200"></div>
          </div>
        </div>
      </section>



      {/* ===== HOW OWNERSIDE CAN SUPPORT YOUR BUSINESS ===== */}
      <section id="services" className="py-[50px] sm:py-16 lg:py-[90px] bg-gradient-to-b from-[#0B1320] to-[#0f1a2e] text-white relative overflow-hidden">
        {/* Background accent */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#0E7C72]/10 to-transparent blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-[41px] sm:mb-14">
            <h2 className="font-heading text-[33px] sm:text-[3.1875rem] font-bold tracking-tight mb-6 leading-[1.05]">
              How OwnerSide can support <br className="hidden lg:inline" />your business
            </h2>
            <p className="text-base text-white/70 max-w-3xl mx-auto leading-relaxed">
              Engage OwnerSide for a focused project, <br className="sm:hidden" />a specific business need, <br className="sm:hidden" />or ongoing executive support.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                icon: BarChart3,
                title: 'Business & Financial Analysis',
                desc: 'Clear analysis to help owners understand performance, risks and opportunities.',
                items: [
                  'Management & financial analysis',
                  'Cash flow and working capital review',
                  'Financial modelling & forecasting',
                  'Contract obligation analysis',
                  'Business performance review',
                ],
              },
              {
                icon: TrendingUp,
                title: <>Management Reporting <br className="hidden lg:inline" />& Performance</>,
                desc: 'Practical reporting tools that turn business information into better decisions.',
                items: [
                  'Management reporting',
                  'KPI development',
                  'Executive dashboards',
                  'Performance tracking',
                  'Financial and operational visibility',
                ],
              },
              {
                icon: Shield,
                title: <>Projects, Transactions <br />& Special Situations</>,
                desc: 'Focused support for important business decisions and one-time projects.',
                items: [
                  'Due diligence',
                  'Financing preparation',
                  'Business transitions',
                  'Transaction support',
                  'Strategic and special projects',
                ],
              },
            ].map((service, i) => {
              const Icon = service.icon;
              return (
              <div key={i} className="group relative">
                <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden h-full transition-all duration-500 hover:bg-white/10 hover:border-[#0E7C72]/50">
                  {/* Top accent line */}
                  <div className="h-[3px] w-full bg-gradient-to-r from-[#0E7C72] to-[#0E7C72]/10"></div>

                  {/* Content */}
                  <div className="p-8 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-xl bg-[#0E7C72]/10 border border-[#0E7C72]/30 flex items-center justify-center group-hover:bg-[#0E7C72]/20 group-hover:border-[#0E7C72] transition-all duration-500">
                        <Icon className="h-4 w-4 text-[#0E7C72]" />
                      </div>
                      <span className="font-mono text-xs text-white/25 tracking-[0.2em]">0{i + 1}</span>
                    </div>
                    <h3 className="font-heading text-2xl lg:text-[23px] font-semibold text-white">
                      {service.title}
                    </h3>
                    <p className="text-white/60 text-[13px] leading-relaxed">
                      {service.desc}
                    </p>
                    <ul className="space-y-[17px] sm:space-y-3 lg:space-y-[5px] pt-2">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[13px] text-white/70">
                          <CheckCircle2 className="h-4 w-4 text-[#0E7C72] mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== TYPICAL DELIVERABLES ===== */}
      <section className="py-[50px] sm:py-16 lg:py-[90px] bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-[44px] sm:gap-16 lg:gap-24">
            <div className="lg:sticky lg:top-24 lg:self-start">
              <h2 className="font-heading text-[33px] sm:text-[3.1875rem] font-bold tracking-tight text-slate-950 mb-6 leading-[1.05]">
                Typical Deliverables
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Support can be structured around a specific project, a defined business need, or an ongoing engagement.
              </p>

              {/* Image accent */}
              <div className="relative h-[288px] sm:h-[20rem] rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets-jai6qajn.emergentagent.net/job_38d96a59-41a3-4967-8296-b7ba8cd6e1a1/artifacts/cck68ndz_2026-08-18%2016.15.14.jpg"
                  alt="Executive team meeting"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: 'center 0%' }}
                />
              </div>
            </div>

            <div>
              <div className="grid gap-6">
                {[
                  { 
                    num: '01', 
                    title: <>Executive Dashboard <br className="sm:hidden" />& Management Report</>,
                    desc: 'Executive summary with key business metrics.'
                  },
                  { 
                    num: '02', 
                    title: 'Cash Flow Visibility & Forecasting',
                    desc: 'Understand upcoming cash needs before they become problems.'
                  },
                  { 
                    num: '03', 
                    title: 'Contract & Obligation Tracking',
                    desc: 'Monitor renewals, commitments and important deadlines.'
                  },
                  { 
                    num: '04', 
                    title: <>Receivables Monitoring <br className="sm:hidden" />& Follow-up</>,
                    desc: 'Track outstanding invoices and support collection efforts.'
                  },
                  { 
                    num: '05', 
                    title: 'KPI Reviews & Performance Tracking',
                    desc: 'Monitor business performance through meaningful indicators.'
                  },
                  { 
                    num: '06', 
                    title: 'Financing & Due Diligence Preparation',
                    desc: 'Keep documentation organized for financing or business transactions.'
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-6 rounded-xl bg-slate-50 border border-slate-200 hover:border-[#0E7C72] transition-colors group"
                  >
                    <span className="font-mono text-sm text-[#0E7C72] font-semibold mt-1 group-hover:scale-110 transition-transform">
                      {item.num}
                    </span>
                    <div className="flex-1">
                      <span className="text-slate-800 font-medium leading-relaxed block mb-1">
                        {item.title}
                      </span>
                      <span className="text-slate-600 text-[13px] leading-relaxed">
                        {item.desc}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS - MINIMAL PREMIUM ===== */}
      <section id="how-it-works" className="py-[50px] sm:py-16 lg:py-[90px] bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-11">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 mb-6">
              <span className="font-mono uppercase tracking-[0.2em] text-xs text-slate-500">
                Our Process
              </span>
            </div>
            <h2 className="font-heading text-[33px] sm:text-[3.1875rem] font-bold tracking-tight text-slate-950 mb-6 leading-[1.05] max-w-3xl mx-auto">
              How It Works
            </h2>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Flexible support built around what <br className="sm:hidden" />your business actually needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-9 max-w-6xl mx-auto">
            {[
              {
                icon: Clipboard,
                title: 'Understand the Business',
                desc: 'We review your priorities, financial information, operations and current challenges.',
              },
              {
                icon: BarChart,
                title: 'Define the Scope',
                desc: 'Together, we determine whether you need a focused project, specific deliverables or ongoing support.',
              },
              {
                icon: ShieldCheck,
                title: 'Deliver & Support',
                desc: 'We provide practical analysis, reporting, coordination and executive support based on the agreed scope.',
              },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <div 
                  key={i} 
                  className="bg-white rounded-2xl px-6 py-7 sm:p-7 lg:p-8 border border-slate-200 hover:border-[#0E7C72] hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  <div className="mb-6">
                    <Icon className="h-9 w-9 text-[#0E7C72] stroke-[1.5]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-950 mb-4 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== WHY OWNERSIDE (STRONGEST SECTION) ===== */}
      <section className="py-[50px] sm:py-16 lg:py-[90px] bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="lg:order-2 relative">
              <div className="relative h-[300px] sm:h-[500px] rounded-3xl overflow-hidden">
                <img
                  src="https://customer-assets-jai6qajn.emergentagent.net/job_38d96a59-41a3-4967-8296-b7ba8cd6e1a1/artifacts/37muedgm_2026-08-18%2016.04.50.jpg"
                  alt="Executive professional"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
              </div>
              {/* Floating card - reduced size and moved up */}
              <div className="absolute -bottom-6 -right-2 sm:-right-6 bg-[#0E7C72] text-white p-4 sm:p-6 rounded-2xl shadow-2xl max-w-[260px]">
                <div className="font-heading text-3xl font-bold mb-0 sm:mb-2">25+</div>
                <div className="text-white/90 text-[13px] leading-snug">Years of Executive Experience</div>
              </div>
            </div>

            <div className="lg:order-1">
              <h2 className="font-heading text-[33px] sm:text-[3.1875rem] font-bold tracking-tight text-slate-950 mb-8 leading-[1.05]">
                The oversight your business is missing
              </h2>
              
              <div className="space-y-6 text-base text-slate-600 leading-relaxed mb-10">
                <p className="lg:pr-0 lg:mr-0 lg:max-w-[115%]">
                  Most business owners already have trusted advisors, but no one is responsible for connecting the full picture from the owner's perspective.
                </p>
                <p>
                  OwnerSide fills that gap by bringing together reporting, contracts, operations and financial visibility—helping owners stay informed and focused on what matters most.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { title: 'Flexible Engagement', desc: 'Project-based or ongoing support' },
                  { title: 'Owner\'s Perspective', desc: 'Independent executive oversight' },
                  { title: 'Decision Support', desc: 'Clearer business decisions' },
                  { title: 'Practical Support', desc: 'Focused on execution, not just advice' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#0E7C72] mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-950 mb-1 text-[15px]">{item.title}</h4>
                      <p className="text-[13px] text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION - BEFORE CLIENT STORIES ===== */}
      <section className="pt-[50px] pb-[50px] sm:pt-[82px] sm:pb-[82px] lg:pt-[98px] lg:pb-[98px] bg-slate-900">
        <div className="max-w-4xl lg:max-w-5xl mx-auto px-6 sm:px-10 text-center">
          {/* Eyebrow */}
          <div className="mb-6">
            <span className="font-mono uppercase tracking-[0.25em] text-xs text-slate-400">
              Ready for More Clarity?
            </span>
          </div>
          
          {/* Headline */}
          <h2 className="font-heading text-[34px] sm:text-5xl lg:text-[3.5rem] font-bold text-white mb-6 leading-[1.1] tracking-tight max-w-3xl mx-auto">
            Ready to take control<br className="hidden lg:inline" /> of your business?
          </h2>
          
          {/* Supporting Text */}
          <p className="text-[15px] sm:text-lg text-slate-300 mb-10 max-w-2xl lg:max-w-none mx-auto leading-relaxed lg:whitespace-nowrap">
            Get clearer insight, stronger coordination, and executive support tailored to your business.
          </p>
          
          {/* CTA Button */}
          <button className="inline-flex items-center px-6 py-3 bg-[#0E7C72] text-white font-semibold rounded-lg hover:bg-[#0c6d64] transition-all duration-200 shadow-sm hover:shadow-md">
            Schedule a Consultation
          </button>
        </div>
      </section>

      {/* ===== VISUAL DIVIDER - PANORAMIC IMAGE ===== */}
      <section className="bg-white">
        <div className="relative w-full h-[204px] sm:h-[340px] overflow-hidden">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_67bee0b4-0865-415b-a742-4d42f51c8383/artifacts/4x4y9l2r_2976aebc-ee0e-412a-b271-1000458067ca.png"
            alt="Business district aerial view"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 60%', filter: 'brightness(1.035)' }}
          />
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-[50px] sm:py-16 lg:py-[90px] bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-[49px] sm:mb-16">
            <h2 className="font-heading text-[33px] sm:text-[3.1875rem] font-bold tracking-tight text-slate-950 mb-6 leading-[1.05]">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-slate-600">
              What business owners usually want to know before getting started.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                q: 'What types of engagements does OwnerSide offer?',
                a: 'OwnerSide can support a specific project, a defined business need, or provide ongoing executive oversight. The engagement structure depends on your priorities and the scope of support required.',
              },
              {
                q: 'How is OwnerSide different from an accountant and a lawyer?',
                a: 'Accountants and lawyers provide expert advice within their own disciplines. OwnerSide connects financial, operational and strategic priorities from the owner\'s perspective, helping the owner stay in control of the bigger picture.',
              },
              {
                q: 'How long does it take to get started?',
                a: 'Most engagements begin with an introductory consultation followed by a structured business review. Once priorities are identified, we can move directly into the agreed scope of work.',
              },
              {
                q: 'What types of businesses do you work with?',
                a: 'We primarily support owner-led businesses in manufacturing, construction, industrial supply, professional services and other operationally complex industries.',
              },
              {
                q: 'Do you work with businesses outside your local area?',
                a: 'Yes. OwnerSide works with businesses remotely across Canada through scheduled virtual meetings, reporting and ongoing communication.',
              },
              {
                q: 'Do you offer both project-based and ongoing support?',
                a: 'Yes. OwnerSide supports both focused projects and ongoing executive engagements. The right structure depends on the business need, complexity and desired level of involvement.',
              },
            ].map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-slate-50 border border-slate-200 rounded-2xl px-6 data-[state=open]:bg-white data-[state=open]:border-[#0E7C72] data-[state=open]:shadow-lg transition-all"
              >
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="font-heading text-lg font-semibold text-slate-950 pr-4">{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-6 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== CTA — CONSULTATION REQUEST ===== */}
      <section id="consultation" className="py-[50px] sm:py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            
            {/* Left: Content */}
            <div className="lg:pr-8">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100">
                  <span className="font-mono uppercase tracking-[0.2em] text-xs text-slate-500">
                    Get in Touch
                  </span>
                </div>
              </div>
              
              <h2 className="font-heading text-[33px] sm:text-[2.6875rem] lg:text-[3.1875rem] font-bold tracking-tight mb-6 leading-[1.1] text-slate-950">
                Schedule a Consultation
              </h2>
              
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                Every business is different. Let's discuss your goals, current challenges, and how OwnerSide can provide the clarity and executive support you need.
              </p>

              {/* Three bullet points */}
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-base text-slate-600">Discuss your business with <br className="sm:hidden" />an experienced advisor</span>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-base text-slate-600">Response within one business day</span>
                </div>
                <div className="flex items-start gap-3">
                  <MessageSquare className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-base text-slate-600">Confidential and obligation-free</span>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <form onSubmit={handleContactSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="consultation-name" className="block text-sm font-medium text-slate-900 mb-2">
                      Full name
                    </label>
                    <Input
                      id="consultation-name"
                      placeholder="Your full name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg h-12 focus:border-[#0E7C72] focus:ring-[#0E7C72]"
                      required
                      data-testid="consultation-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="consultation-email" className="block text-sm font-medium text-slate-900 mb-2">
                      Email
                    </label>
                    <Input
                      id="consultation-email"
                      type="email"
                      placeholder="you@example.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg h-12 focus:border-[#0E7C72] focus:ring-[#0E7C72]"
                      required
                      data-testid="consultation-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="consultation-phone" className="block text-sm font-medium text-slate-900 mb-2">
                    Phone <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <Input
                    id="consultation-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg h-12 focus:border-[#0E7C72] focus:ring-[#0E7C72]"
                    data-testid="consultation-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="consultation-message" className="block text-sm font-medium text-slate-900 mb-2">
                    How can we help? <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <Textarea
                    id="consultation-message"
                    placeholder="Tell us about your business, current priorities, or the support you're looking for..."
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg min-h-[120px] focus:border-[#0E7C72] focus:ring-[#0E7C72] resize-none"
                    data-testid="consultation-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={contactLoading}
                  className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-[#0E7C72] text-white font-semibold rounded-full hover:bg-[#0c6d64] transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="consultation-submit-button"
                >
                  {contactLoading ? 'Submitting...' : 'Schedule a Consultation'}
                </button>

                <p className="text-sm text-slate-400 text-center mt-4">
                  Your information will remain confidential.
                </p>
              </form>
            </div>

          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="relative bg-gradient-to-br from-[#090D17] via-[#0a1220] to-[#090D17] text-white py-16 overflow-hidden">
        {/* Grid background pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDMpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-12 mb-12">
            {/* Brand */}
            <div className="max-w-sm">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="https://customer-assets-jai6qajn.emergentagent.net/job_38d96a59-41a3-4967-8296-b7ba8cd6e1a1/artifacts/rrpmz7yf_logo%20%287%29.png" alt="OwnerSide" className="h-9 w-9 object-contain" data-testid="footer-logo" />
                <span className="font-heading text-xl font-bold text-white">OwnerSide</span>
              </div>
              <p className="text-[13px] text-white/60 leading-relaxed mb-6 max-w-sm">
                An external Owner's Office helping business owners understand, control and improve their business.
              </p>
              <div className="flex items-center gap-2.5 text-sm text-white/70">
                <Mail className="h-4 w-4 text-[#0E7C72] flex-shrink-0" />
                <a href="mailto:info@owner-side.com" className="hover:text-[#0E7C72] transition-colors">
                  info@owner-side.com
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-12 sm:gap-16 lg:gap-48">
              {/* Quick Links */}
              <div>
                <h4 className="font-heading text-sm font-semibold text-white mb-4">Quick Links</h4>
                <div className="space-y-2.5 text-sm text-white/60">
                  <div>
                    <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-[#0E7C72] transition-colors">
                      Service Areas
                    </button>
                  </div>
                  <div>
                    <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-[#0E7C72] transition-colors">
                      How It Works
                    </button>
                  </div>
                  <div>
                    <button onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-[#0E7C72] transition-colors">
                      Consultation
                    </button>
                  </div>
                  <div>
                    <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-[#0E7C72] transition-colors">
                      FAQ
                    </button>
                  </div>
                </div>
              </div>

              {/* Legal */}
              <div>
                <h4 className="font-heading text-sm font-semibold text-white mb-4">Legal</h4>
                <div className="space-y-2.5 text-sm text-white/60">
                  <div><Link to="/privacy" className="hover:text-[#0E7C72] transition-colors">Privacy Policy</Link></div>
                  <div><Link to="/terms" className="hover:text-[#0E7C72] transition-colors">Terms of Service</Link></div>
                  <div><Link to="/cookies" className="hover:text-[#0E7C72] transition-colors">Advertising & Cookies</Link></div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex justify-center items-center">
            <p className="text-xs text-white/40 text-center">
              &copy; {new Date().getFullYear()} OwnerSide Business Services Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
